import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OrderTile from '../components/OrderTile';
import '../components/OrderTile.css';
import './WalletPage.css';

function BottomSheet({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="wallet-bottomsheet-bg" onClick={onClose}>
      <div className="wallet-bottomsheet" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function Dialog({ open, title, content, actions }) {
  if (!open) return null;
  return (
    <div className="wallet-dialog-bg">
      <div className="wallet-dialog">
        <div className="wallet-dialog-title">{title}</div>
        <div className="wallet-dialog-content">{content}</div>
        <div className="wallet-dialog-actions">{actions}</div>
      </div>
    </div>
  );
}

export default function WalletPage() {
  const [tab, setTab] = useState(0); // 0: Ready, 1: History
  const [readyOrders, setReadyOrders] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false });
  const [bottomSheet, setBottomSheet] = useState({ open: false, order: null });
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
  }, []);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    fetch(`https://devbite.vercel.app/api/wallet/orders_ready?uid=${user.uid}`)
      .then(res => res.json())
      .then(data => setReadyOrders(data.orders || []));
    fetch(`https://devbite.vercel.app/api/wallet/balance?uid=${user.uid}`)
      .then(res => res.json())
      .then(data => setBalance(data.balance || 0));
    setLoading(false);
  }, [user]);

  const handleRequestAll = async () => {
    if (!user) return;
    if (!user.phoneNumber || user.phoneNumber.length !== 10 || !user.phoneNumber.startsWith('0')) {
      setDialog({
        open: true,
        title: 'Invalid Phone',
        content: 'Your phone number is invalid or missing. Cannot proceed.',
        actions: <button onClick={() => setDialog({ open: false })}>OK</button>,
      });
      return;
    }
    setDialog({
      open: true,
      title: 'Confirm Withdrawal',
      content: (
        <>
          <div>You are about to withdraw:</div>
          <div style={{ fontSize: 24, color: '#6c2eb7', fontWeight: 'bold', margin: '10px 0' }}>
            UGX {balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
          <div>To number:</div>
          <div style={{ fontSize: 18 }}>{user.phoneNumber}</div>
        </>
      ),
      actions: (
        <>
          <button onClick={() => setDialog({ open: false })}>Cancel</button>
          <button className="wallet-dialog-confirm" onClick={async () => {
            setDialog({ open: true, title: 'Processing', content: 'Requesting all commissions...', actions: null });
            const response = await fetch('https://devbite.vercel.app/request_commission', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                orders: readyOrders,
                userData: user
              })
            });
            const data = await response.json();
            if (response.ok && data.success) {
              setDialog({
                open: true,
                title: 'Success',
                content: 'All commissions requested successfully!',
                actions: <button onClick={() => window.location.reload()}>OK</button>,
              });
            } else {
              setDialog({
                open: true,
                title: 'Error',
                content: data.error || data.message || 'Failed to request commissions.',
                actions: <button onClick={() => setDialog({ open: false })}>OK</button>,
              });
            }
          }}>Confirm</button>
        </>
      ),
    });
  };

  // Single order commission request from bottom sheet
  const handleRequestCommission = async (order) => {
    if (!user) return;
    const commission = (parseFloat(order.cakePrice || order.price || 0) || 0) * 0.15;
    if (!user.phoneNumber || user.phoneNumber.length !== 10 || !user.phoneNumber.startsWith('0')) {
      setDialog({
        open: true,
        title: 'Invalid Phone',
        content: 'Your phone number is invalid or missing. Cannot proceed.',
        actions: <button onClick={() => setDialog({ open: false })}>OK</button>,
      });
      return;
    }
    setDialog({
      open: true,
      title: 'Confirm Commission Request',
      content: (
        <>
          <div>Request commission for this order?</div>
          <div style={{ fontSize: 20, color: '#6c2eb7', fontWeight: 'bold', margin: '10px 0' }}>
            UGX {commission.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
          <div>To number:</div>
          <div style={{ fontSize: 15 }}>{user.phoneNumber}</div>
        </>
      ),
      actions: (
        <>
          <button onClick={() => setDialog({ open: false })}>Cancel</button>
          <button className="wallet-dialog-confirm" onClick={async () => {
            setDialog({ open: true, title: 'Processing', content: 'Requesting commission...', actions: null });
            const response = await fetch('https://devbite.vercel.app/request_commission', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                orders: [order],
                userData: user
              })
            });
            const data = await response.json();
            if (response.ok && data.success) {
              setDialog({
                open: true,
                title: 'Success',
                content: 'Commission requested successfully!',
                actions: <button onClick={() => window.location.reload()}>OK</button>,
              });
            } else {
              setDialog({
                open: true,
                title: 'Error',
                content: data.error || data.message || 'Failed to request commission.',
                actions: <button onClick={() => setDialog({ open: false })}>OK</button>,
              });
            }
          }}>Confirm</button>
        </>
      ),
    });
    setBottomSheet({ open: false, order: null });
  };

  // Bottom sheet content
  const renderBottomSheet = () => {
    const order = bottomSheet.order;
    if (!order) return null;
    return (
      <div>
        <div className="wallet-bottomsheet-handle" />
        <div className="wallet-bottomsheet-list">
          <button className="wallet-bottomsheet-listitem" onClick={() => handleRequestCommission(order)}>
            <span className="wallet-bottomsheet-icon">💸</span> <b>Request Commission</b>
          </button>
          <button className="wallet-bottomsheet-listitem" onClick={() => { setBottomSheet({ open: false, order: null }); navigate(`/order/${order.id}`); }}>
            <span className="wallet-bottomsheet-icon">📄</span> <b>View Order</b>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="wallet-page-bg">
      <div className="wallet-header">
        <button className="wallet-back" onClick={() => navigate(-1)}>&larr;</button>
        <div className="wallet-avatar">
          {user && user.photoURL ? (
            <img src={user.photoURL} alt="User" />
          ) : (
            <span role="img" aria-label="user">👤</span>
          )}
        </div>
        <button className="wallet-request-all" onClick={handleRequestAll}>Request All</button>
      </div>
      <div className="wallet-balance-label">Available Balance</div>
      <div className="wallet-balance">UGX {balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
      <div className="wallet-tabs">
        <button className={tab === 0 ? 'wallet-tab wallet-tab-active' : 'wallet-tab'} onClick={() => setTab(0)}>Ready</button>
        <button className={tab === 1 ? 'wallet-tab' : 'wallet-tab wallet-tab-inactive'} onClick={() => setTab(1)}>History</button>
      </div>
      <div className="wallet-tab-underline">
        <div className={tab === 0 ? 'wallet-tab-indicator' : 'wallet-tab-indicator wallet-tab-indicator-right'}></div>
      </div>
      <div className="wallet-orders-list">
        {loading ? (
          <div className="wallet-loading">Loading...</div>
        ) : tab === 0 ? (
          readyOrders.length === 0 ? <div className="wallet-empty">No ready orders.</div> :
            readyOrders.map(order => (
              <div key={order.id} onClick={() => setBottomSheet({ open: true, order })}>
                <OrderTile order={order} />
              </div>
            ))
        ) : (
          <div className="wallet-empty">History coming soon.</div>
        )}
      </div>
      <Dialog {...dialog} />
      <BottomSheet open={bottomSheet.open} onClose={() => setBottomSheet({ open: false, order: null })}>
        {renderBottomSheet()}
      </BottomSheet>
    </div>
  );
}
